import { generateDpopKeyPair } from './proof';

/**
 * The browser profile's DPoP key pair, kept in IndexedDB. One key per profile,
 * shared by every tab, created on first use and never rotated or deleted (a
 * revoked session is dead regardless of the key, and keeping it avoids
 * cross-tab races). A non-extractable `CryptoKey` survives the structured
 * clone IndexedDB does and comes back still non-extractable.
 *
 * This module **never throws and never falls back to an in-memory key**: an
 * unusable store resolves `{ keyPair: null, reason }` and the caller sends
 * plain bearer tokens. A per-tab key would make tabs fight over the bound
 * refresh token, which is worse than no key at all.
 *
 * It also does no telemetry — the signer in ./index.js memoises the result and
 * reports it once per page load, so a reason can never be reported twice.
 *
 * `indexedDB` and `subtle` are parameters rather than globals so the same code
 * runs on the page and under jest's jsdom environment.
 */

const DATABASE_NAME = 'formue-auth';
const DATABASE_VERSION = 1;
const STORE_NAME = 'keys';
const RECORD_ID = 'dpop';

const REASONS = ['no-subtle', 'no-indexeddb', 'open-failed', 'open-timeout'];

/**
 * Internal error carrying the `reason` the public result should report.
 * `constraint` is the one internal-only reason: another tab stored a key
 * first, which is a normal race, not a failure.
 */
const keyStoreError = (reason) => {
  const error = new Error(`DPoP key store failed: ${reason}`);
  error.reason = reason;
  return error;
};

const toReason = (error) =>
  REASONS.includes(error?.reason) ? error.reason : 'unknown';

const closeDatabase = (database) => {
  try {
    database.close();
  } catch {
    // A database that cannot be closed is nothing the caller can act on.
  }
};

/**
 * Open (and on first use create) the database, racing the request against
 * `timeoutMs` — Safari is known to leave `open` hanging forever in some
 * private-mode states, and a hung login is worse than a bearer one. A request
 * that answers after we gave up has its database closed.
 */
export const openDatabase = (indexedDB, timeoutMs) =>
  new Promise((resolve, reject) => {
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(keyStoreError('open-timeout'));
    }, timeoutMs);

    const succeed = (database) => {
      if (settled) {
        closeDatabase(database);
        return;
      }
      settled = true;
      clearTimeout(timer);
      resolve(database);
    };

    const fail = (reason) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(keyStoreError(reason));
    };

    let request;
    try {
      request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    } catch {
      fail('open-failed');
      return;
    }

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => succeed(request.result);
    request.onerror = () => fail('open-failed');
    request.onblocked = () => fail('open-failed');
  });

/**
 * Promisify one `IDBRequest`, raced against the same timer as `open` — a
 * transaction that never settles reports `open-timeout` too, since from the
 * caller's side it is the same "the store is unusable" situation.
 */
const runRequest = (start, timeoutMs) =>
  new Promise((resolve, reject) => {
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(keyStoreError('open-timeout'));
    }, timeoutMs);

    const settle = (finish, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      finish(value);
    };

    let request;
    try {
      request = start();
    } catch (error) {
      settle(
        reject,
        keyStoreError(
          error?.name === 'ConstraintError' ? 'constraint' : 'unknown'
        )
      );
      return;
    }

    request.onsuccess = () => settle(resolve, request.result);
    request.onerror = () => {
      const name = request.error?.name || request.transaction?.error?.name;
      settle(
        reject,
        keyStoreError(name === 'ConstraintError' ? 'constraint' : 'unknown')
      );
    };
  });

export const readKeyPair = async (database, timeoutMs) => {
  const record = await runRequest(
    () =>
      database
        .transaction(STORE_NAME, 'readonly')
        .objectStore(STORE_NAME)
        .get(RECORD_ID),
    timeoutMs
  );

  if (!record || !record.privateKey || !record.publicKey) {
    return null;
  }
  return { privateKey: record.privateKey, publicKey: record.publicKey };
};

/**
 * `add`, never `put`: if another tab stored a pair between our read and our
 * write, the write must fail (`ConstraintError`) so we adopt that tab's key
 * instead of overwriting a key the provider may already have bound a session
 * to.
 */
export const storeKeyPair = (database, keyPair, timeoutMs) =>
  runRequest(
    () =>
      database
        .transaction(STORE_NAME, 'readwrite')
        .objectStore(STORE_NAME)
        .add({
          id: RECORD_ID,
          privateKey: keyPair.privateKey,
          publicKey: keyPair.publicKey,
        }),
    timeoutMs
  );

/**
 * Get-or-create the profile's key pair. Resolves
 * `{ keyPair: { privateKey, publicKey }, reason: null }` on success, or
 * `{ keyPair: null, reason }` with `reason` one of `no-subtle`,
 * `no-indexeddb`, `open-failed`, `open-timeout`, `unknown`.
 */
export const loadOrCreateDpopKeyPair = async ({
  indexedDB,
  subtle,
  timeoutMs = 3000,
} = {}) => {
  if (!subtle) {
    return { keyPair: null, reason: 'no-subtle' };
  }
  if (!indexedDB) {
    return { keyPair: null, reason: 'no-indexeddb' };
  }

  let database = null;
  try {
    database = await openDatabase(indexedDB, timeoutMs);

    const stored = await readKeyPair(database, timeoutMs);
    if (stored) {
      return { keyPair: stored, reason: null };
    }

    const generated = await generateDpopKeyPair(subtle);
    try {
      await storeKeyPair(database, generated, timeoutMs);
    } catch (error) {
      if (error?.reason !== 'constraint') {
        throw error;
      }
      // Another tab won the race — use the pair it stored.
      const winner = await readKeyPair(database, timeoutMs);
      if (!winner) {
        throw error;
      }
      return { keyPair: winner, reason: null };
    }

    return { keyPair: generated, reason: null };
  } catch (error) {
    return { keyPair: null, reason: toReason(error) };
  } finally {
    if (database) {
      closeDatabase(database);
    }
  }
};
